import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { ArrowLeft, CheckCircle, Sparkle, ShieldCheck, Globe, CurrencyDollar, Copy } from '@phosphor-icons/react';
import Header from '@/components/Header';
import PremiumFooter from '@/components/PremiumFooter';
import { WELLNESS_CATEGORIES, COUNTRIES, LANGUAGES, MODES, formatPrice } from '@/data/wellness';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const WellnessJoinPage = ({ user }) => {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [form, setForm] = useState({
    full_name: '',
    email: '',
    phone: '',
    category_id: 'nutrition',
    professional_type: 'Nutritionist',
    country: 'IN',
    city: '',
    state: '',
    years_experience: 0,
    headline: '',
    bio: '',
    modes: ['online'],
    service_radius_km: 10,
    price_from: 0,
    languages: ['English'],
  });

  const selectedCategory = useMemo(() => WELLNESS_CATEGORIES.find((c) => c.id === form.category_id), [form.category_id]);
  const selectedCountry = useMemo(() => COUNTRIES.find((c) => c.code === form.country), [form.country]);
  const currency = selectedCountry?.currency || 'USD';

  const setField = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const onCategoryChange = (id) => {
    const cat = WELLNESS_CATEGORIES.find((c) => c.id === id);
    setForm((s) => ({ ...s, category_id: id, professional_type: cat.types[0] }));
  };

  const toggleMode = (m) => {
    setForm((s) => ({
      ...s,
      modes: s.modes.includes(m) ? s.modes.filter((x) => x !== m) : [...s.modes, m],
    }));
  };

  const toggleLanguage = (lang) => {
    setForm((s) => ({
      ...s,
      languages: s.languages.includes(lang) ? s.languages.filter((x) => x !== lang) : [...s.languages, lang],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.phone || !form.city || !form.modes.length) {
      toast.error('Please fill all required fields and select at least one mode');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/wellness/join-request`, {
        ...form,
        years_experience: parseInt(form.years_experience) || 0,
        service_radius_km: parseInt(form.service_radius_km) || 10,
        price_from: parseFloat(form.price_from) || 0,
      });
      setConfirmation(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = "w-full bg-white border border-stone-200 focus:border-[#1A4331] focus:ring-4 focus:ring-[#1A4331]/10 rounded-2xl px-4 py-3 outline-none transition-all text-[15px] text-[#1C1917] placeholder:text-stone-400";
  const labelCls = "block text-[11px] font-bold uppercase tracking-[0.14em] text-stone-500 mb-1.5";

  if (confirmation) {
    return (
      <div className="min-h-screen bg-[#FAFAF7]" data-testid="wellness-join-success">
        <Header user={user} />
        <div className="max-w-2xl mx-auto py-16 md:py-24 px-6 text-center">
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: 'spring', stiffness: 200, damping: 14 }}
            className="w-24 h-24 bg-gradient-to-br from-[#34D399] to-[#10B981] rounded-full flex items-center justify-center mx-auto mb-6 shadow-xl shadow-[#10B981]/30"
          >
            <CheckCircle size={48} weight="fill" className="text-white" />
          </motion.div>
          <h1 className="text-4xl md:text-5xl font-semibold text-[#1C1917] font-heading tracking-tight mb-4">Application received!</h1>
          <p className="text-lg text-stone-600 leading-relaxed mb-8">
            Thanks for joining Nova Wellness. Our team will review your profile within <strong>2 business days</strong> and notify you at <strong>{form.email}</strong>.
          </p>
          <div className="bg-white border border-stone-100 rounded-3xl p-6 max-w-md mx-auto mb-8">
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-stone-500 mb-1">Your Application ID</p>
            <p className="text-xl font-bold text-[#1A4331] font-heading tracking-tight" data-testid="join-application-id">{confirmation.professional_id}</p>
            <p className="text-sm text-stone-500 mt-4">Status: <span className="inline-flex items-center gap-1 text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full text-xs font-semibold"><Sparkle size={10} weight="fill" /> Pending Verification</span></p>
          </div>

          {confirmation.access_token && (
            <div className="bg-gradient-to-br from-[#F0F7F1] to-white border border-[#1A4331]/15 rounded-3xl p-6 max-w-lg mx-auto mb-8 text-left" data-testid="join-access-token-card">
              <p className="text-xs uppercase tracking-[0.16em] text-[#1A4331] font-bold mb-2 flex items-center gap-2">
                <ShieldCheck size={14} weight="fill" /> Your Professional Access Token
              </p>
              <div className="flex items-center gap-2 bg-white border border-stone-200 rounded-2xl p-3 mb-3">
                <code className="flex-1 text-xs font-mono text-stone-700 truncate select-all" data-testid="access-token-value">{confirmation.access_token}</code>
                <button
                  onClick={() => { navigator.clipboard.writeText(confirmation.access_token); toast.success('Copied to clipboard'); }}
                  className="text-[#1A4331] hover:bg-[#F0F7F1] p-1.5 rounded-lg transition-all"
                  data-testid="copy-token-btn"
                >
                  <Copy size={16} weight="duotone" />
                </button>
              </div>
              <p className="text-xs text-stone-600 leading-relaxed mb-3">
                <strong>Save this token now.</strong> You&apos;ll use it to log into your Professional Dashboard, manage your profile, upload credentials, and respond to consultation requests.
              </p>
              <button
                onClick={() => navigate(`/wellness/pro/login?token=${confirmation.access_token}`)}
                className="w-full bg-[#1A4331] text-white py-2.5 rounded-full font-semibold hover:bg-[#2A5A44] transition-all text-sm inline-flex items-center justify-center gap-2"
                data-testid="go-to-pro-dashboard-btn"
              >
                Open my Professional Dashboard →
              </button>
            </div>
          )}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button onClick={() => navigate('/wellness')} className="bg-white border border-stone-200 text-stone-700 px-6 py-3 rounded-full font-semibold hover:border-[#1A4331] transition-all">
              Back to Wellness
            </button>
            <button onClick={() => navigate('/')} className="bg-[#1A4331] text-white px-6 py-3 rounded-full font-semibold hover:bg-[#2A5A44] transition-all">
              Explore Nova Foods
            </button>
          </div>
        </div>
        <PremiumFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF7]" data-testid="wellness-join-page">
      <Header user={user} />

      <section className="max-w-5xl mx-auto px-6 pt-10 pb-16">
        <button onClick={() => navigate('/wellness')} className="text-sm text-stone-500 hover:text-[#1A4331] mb-6 inline-flex items-center gap-1">
          <ArrowLeft size={14} weight="bold" /> Back to Wellness
        </button>

        <div className="grid grid-cols-1 md:grid-cols-[1fr_320px] gap-8">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#C05C3D] mb-3">Join Nova Wellness</p>
            <h1 className="text-4xl md:text-5xl font-semibold text-[#1C1917] font-heading tracking-tight mb-3">Grow your practice with Nova</h1>
            <p className="text-stone-600 mb-8 leading-relaxed max-w-lg">Fill in the basics to create your profile. It&apos;s <strong>free</strong>, takes 2 minutes, and our team will verify you within 2 business days.</p>

            <form onSubmit={handleSubmit} className="space-y-5 bg-white rounded-3xl p-6 md:p-8 border border-stone-100 shadow-sm" data-testid="join-form">
              {/* Basic */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><label className={labelCls}>Full Name *</label><input required className={inputCls} value={form.full_name} onChange={setField('full_name')} data-testid="join-full-name" /></div>
                <div><label className={labelCls}>Email *</label><input required type="email" className={inputCls} value={form.email} onChange={setField('email')} data-testid="join-email" /></div>
                <div><label className={labelCls}>Phone *</label><input required type="tel" className={inputCls} value={form.phone} onChange={setField('phone')} placeholder="+91 98..." data-testid="join-phone" /></div>
                <div><label className={labelCls}>Years of Experience</label><input type="number" min={0} className={inputCls} value={form.years_experience} onChange={setField('years_experience')} data-testid="join-years" /></div>
              </div>

              {/* Category */}
              <div>
                <label className={labelCls}>Category *</label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {WELLNESS_CATEGORIES.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => onCategoryChange(c.id)}
                      className={`text-left p-3 rounded-2xl border transition-all ${form.category_id === c.id ? 'border-[#1A4331] bg-[#F0F7F1]' : 'border-stone-200 bg-white hover:border-[#1A4331]/40'}`}
                      data-testid={`join-cat-${c.id}`}
                    >
                      <c.icon size={20} weight="duotone" style={{ color: c.color }} />
                      <p className="text-sm font-semibold text-[#1C1917] mt-1.5 leading-tight">{c.name}</p>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className={labelCls}>Professional Type *</label>
                <select className={inputCls} value={form.professional_type} onChange={setField('professional_type')} data-testid="join-pro-type">
                  {selectedCategory?.types.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>

              {/* Location */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div><label className={labelCls}>Country *</label>
                  <select className={inputCls} value={form.country} onChange={setField('country')} data-testid="join-country">
                    {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
                  </select>
                </div>
                <div><label className={labelCls}>State / Province</label><input className={inputCls} value={form.state} onChange={setField('state')} data-testid="join-state" /></div>
                <div><label className={labelCls}>City *</label><input required className={inputCls} value={form.city} onChange={setField('city')} data-testid="join-city" /></div>
              </div>

              {/* Modes */}
              <div>
                <label className={labelCls}>Service Modes * <span className="normal-case tracking-normal text-stone-400 font-normal">(select at least one)</span></label>
                <div className="flex flex-wrap gap-2">
                  {MODES.map((m) => (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => toggleMode(m.id)}
                      className={`px-4 py-2 rounded-full text-sm font-semibold border transition-all ${form.modes.includes(m.id) ? 'bg-[#1A4331] text-white border-[#1A4331]' : 'bg-white text-stone-700 border-stone-200 hover:border-[#1A4331]'}`}
                      data-testid={`join-mode-${m.id}`}
                    >
                      {m.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Languages */}
              <div>
                <label className={labelCls}>Languages Spoken</label>
                <div className="flex flex-wrap gap-1.5">
                  {LANGUAGES.map((lang) => (
                    <button
                      key={lang}
                      type="button"
                      onClick={() => toggleLanguage(lang)}
                      className={`px-3 py-1 rounded-full text-xs font-semibold border transition-all ${form.languages.includes(lang) ? 'bg-[#1A4331] text-white border-[#1A4331]' : 'bg-white text-stone-700 border-stone-200 hover:border-[#1A4331]'}`}
                    >
                      {lang}
                    </button>
                  ))}
                </div>
              </div>

              {/* Bio */}
              <div>
                <label className={labelCls}>Headline</label>
                <input className={inputCls} value={form.headline} onChange={setField('headline')} placeholder="e.g., Certified RYT-500 · Vinyasa & Meditation" data-testid="join-headline" />
              </div>
              <div>
                <label className={labelCls}>Short Bio</label>
                <textarea rows={3} className={`${inputCls} resize-none`} value={form.bio} onChange={setField('bio')} placeholder="Tell clients about your approach & experience" data-testid="join-bio" />
              </div>

              {/* Pricing */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Starting Price (per session)</label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-3 flex items-center text-stone-400 font-semibold">{currency}</span>
                    <input type="number" min={0} step="0.01" className={`${inputCls} pl-16`} value={form.price_from} onChange={setField('price_from')} data-testid="join-price" />
                  </div>
                </div>
                <div>
                  <label className={labelCls}>Service Radius (km)</label>
                  <input type="number" min={0} className={inputCls} value={form.service_radius_km} onChange={setField('service_radius_km')} data-testid="join-radius" />
                </div>
              </div>

              <div className="pt-2">
                <button type="submit" disabled={submitting} className="w-full bg-[#1A4331] text-white py-4 rounded-full font-bold hover:bg-[#2A5A44] transition-all shadow-md disabled:opacity-50 flex items-center justify-center gap-2" data-testid="join-submit-btn">
                  {submitting ? 'Submitting...' : 'Submit for Verification'}
                </button>
                <p className="text-xs text-stone-500 mt-3 text-center">By submitting, you agree to Nova&apos;s Professional Code of Conduct.</p>
              </div>
            </form>
          </div>

          {/* Sidebar benefits */}
          <aside className="hidden md:block">
            <div className="sticky top-24 space-y-4">
              <div className="bg-gradient-to-br from-[#1A4331] to-[#2A5A44] text-white rounded-3xl p-6">
                <p className="text-3xl font-semibold font-heading tracking-tight">Free</p>
                <p className="text-sm text-stone-200 mt-1">Registration & profile</p>
              </div>
              {[
                { icon: ShieldCheck, title: 'Nova Verification', desc: 'Trusted badge that builds client confidence' },
                { icon: Globe, title: 'Global Reach', desc: 'Online clients from anywhere in the world' },
                { icon: CurrencyDollar, title: '90% Payout', desc: 'Nova keeps just 10% commission' },
              ].map((b) => (
                <div key={b.title} className="bg-white rounded-3xl p-5 border border-stone-100">
                  <b.icon size={22} weight="duotone" className="text-[#1A4331]" />
                  <p className="mt-2 font-semibold text-[#1C1917]">{b.title}</p>
                  <p className="text-sm text-stone-500 leading-relaxed mt-0.5">{b.desc}</p>
                </div>
              ))}
              <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-3xl p-4 text-xs leading-relaxed">
                <strong>What happens next?</strong> Our team reviews your profile, and once approved, you&apos;ll appear in the Nova Wellness marketplace. The full onboarding wizard (credentials, services, availability) opens after approval.
              </div>
            </div>
          </aside>
        </div>
      </section>

      <PremiumFooter />
    </div>
  );
};

export default WellnessJoinPage;
